/**
 * AgendaList — List of meeting agendas; clicking a row or creating opens the editor in a side panel
 */

import { useState } from 'react';
import { Plus, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { useTeams } from '@/features/rocks/hooks/useTeams';
import { useAgendas, useCreateAgenda } from '../hooks/useAgendas';
import { AgendaEditor } from './AgendaEditor';

// =============================================================================
// AgendaList
// =============================================================================

interface AgendaListProps {
  teamId?: string;
}

export function AgendaList({ teamId }: AgendaListProps) {
  const { data, isLoading } = useAgendas({ team_id: teamId });
  const { data: teams = [] } = useTeams();
  const createAgenda = useCreateAgenda();

  const [editingAgendaId, setEditingAgendaId] = useState<string | null>(null);

  const agendas = data?.agendas ?? [];
  const teamName = (id: string | null) => (id ? (teams.find((t) => t.id === id)?.name ?? '—') : '—');

  const handleCreate = async () => {
    try {
      const agenda = await createAgenda.mutateAsync({ title: 'New Agenda', team_id: teamId });
      setEditingAgendaId(agenda.id);
    } catch {
      toast.error('Failed to create agenda');
    }
  };

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : agendas.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-border rounded-lg">
          <FileText className="h-8 w-8 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No agendas yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Team</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agendas.map((agenda) => (
                <TableRow
                  key={agenda.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setEditingAgendaId(agenda.id)}
                >
                  <TableCell className="font-medium">{agenda.title}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {teamName(agenda.team_id)}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {new Date(agenda.created_at).toLocaleDateString('en-CA', {
                      timeZone: 'America/Montreal',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create button */}
      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCreate}
          disabled={createAgenda.isPending}
        >
          <Plus className="h-4 w-4 mr-1.5" />
          {createAgenda.isPending ? 'Creating...' : 'Create Agenda'}
        </Button>
      </div>

      {/* Editor Side Panel */}
      <Sheet open={!!editingAgendaId} onOpenChange={(open) => { if (!open) setEditingAgendaId(null); }}>
        <SheetContent
          side="right"
          className="w-full sm:!max-w-2xl overflow-y-auto"
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target?.closest('[role="listbox"], [data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
          }}
        >
          <SheetHeader className="mb-6">
            <SheetTitle>Edit Agenda</SheetTitle>
          </SheetHeader>
          {editingAgendaId && <AgendaEditor agendaId={editingAgendaId} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
